// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { Modal, Dropdown, TextInput, StatusBanner, useToast } from '@loa/ui-components';
import type { DropdownOption } from '@loa/ui-components';
import { agencyCodeApi } from '../api/agencyCodeApi';
import { brokerageConfigApi } from '../api/brokerageConfigApi';

type AddAgencyCodeModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  advisorId: string;
  brokerageId: string;
  existingProviderIds: string[];
}>;

export function AddAgencyCodeModal({
  open,
  onClose,
  onSuccess,
  advisorId,
  brokerageId,
  existingProviderIds,
}: AddAgencyCodeModalProps) {
  const { showToast } = useToast();
  const [providerId, setProviderId] = useState('');
  const [code, setCode] = useState('');
  const [providerOptions, setProviderOptions] = useState<DropdownOption[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !brokerageId) return;

    const fetchProviders = async () => {
      setProvidersLoading(true);
      try {
        const data = await brokerageConfigApi.getEnabledProviders(brokerageId);
        const filtered = data.content.filter(
          (bp) => !existingProviderIds.includes(bp.provider.id)
        );
        setProviderOptions(
          filtered.map((bp) => ({
            value: bp.provider.id,
            text: bp.provider.name,
          }))
        );
      } catch {
        setError('Failed to load providers');
      } finally {
        setProvidersLoading(false);
      }
    };

    fetchProviders();
  }, [open, brokerageId, existingProviderIds]);

  const handleClose = () => {
    setProviderId('');
    setCode('');
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    const trimmedCode = code.trim();

    if (!providerId) {
      setError('Please select a provider');
      return;
    }
    if (!trimmedCode) {
      setError('Agency code is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await agencyCodeApi.createAgencyCode(advisorId, providerId, trimmedCode);
      showToast('Agency code added successfully.', 'success');
      onSuccess();
      handleClose();
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to add agency code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} size="medium">
      <Modal.Header icon="add-square" title="Add agency code" onClose={handleClose} />
      <Modal.Content>
        {error && (
          <StatusBanner
            variant="error"
            message={error}
            visible
            position="inline"
            dismissible={false}
          />
        )}

        <div className="flex flex-col gap-xl">
          <Dropdown
            label="Provider"
            placeholder="Select a provider"
            options={providerOptions}
            value={providerId}
            onChange={(value) => setProviderId(value)}
            disabled={providersLoading || loading}
            search
            fluid
          />

          <TextInput
            label="Agency Code"
            placeholder="Enter your agency code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={50}
            required
            fluid
          />
        </div>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: handleClose, disabled: loading }}
        primaryAction={{ label: 'Save', onClick: () => { void handleSave(); }, loading, disabled: loading }}
      />
    </Modal>
  );
}
